import { useEffect, useMemo, useRef, useState } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { API_BASE_URL } from '@/lib/api'
import { useBrand } from '@/hooks/useBrand'

interface Preview {
  widgetKey: string
  firmName: string
  primaryColor: string
  welcomeMessage: string | null
  assistantName: string | null
  practiceAreas: string[]
  tagline: string
  wizardConfigured: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Public landing page for affiliate sales demos. The seller shares
// app.legallady.ai/preview/<sandbox-slug> with a prospect during a call to
// demo the chat widget live, configured for the prospect's firm via the
// sandbox's current agent.
//
// Aesthetic: refined editorial — boutique-counsel gravitas, not personal-injury
// billboard. Cormorant Garamond display, Manrope body, deep navy on parchment
// with a restrained burnished-gold accent. Asymmetric editorial hero, Roman-
// numbered sections, monogram logo derived from the firm's initials.
// ─────────────────────────────────────────────────────────────────────────────

const FONT_LINK_ID = 'sandbox-preview-fonts'
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Manrope:wght@300;400;500;600&display=swap'

// "Bob Severn" → "BS" · "Bob Jones" → "BJ" · "Severn" → "SE" · "Smith & Co LLP" → "SC"
function deriveInitials(firmName: string): string {
  if (!firmName) return 'LF'
  const cleaned = firmName
    .replace(/\b(LLP|LLC|PC|PLLC|PA|PLC|& Associates|Law Firm|Law Group|Law Office|Law|Attorneys?|Esq\.?|& Co\.?|Inc\.?|Demo Workspace)\b/gi, '')
    .replace(/[&.,/-]+/g, ' ')
    .trim()
  const words = cleaned.split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return firmName.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || 'LF'
  }
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

// I, II, III, IV, V — capped at 12 since no firm needs more practice-area numerals
function toRoman(n: number): string {
  const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII']
  return numerals[n - 1] || String(n)
}

// Per-area copy that's specific enough to feel real but generic enough that no
// real firm could read it as a misrepresentation. Falls back to a neutral line
// for areas we don't recognize.
const AREA_COPY: Record<string, string> = {
  'Personal Injury': 'When negligence upends a life, recovery is more than a verdict. We pursue the settlement that lets you stop fighting and start healing.',
  'Criminal Defense': 'A charge is not a conviction. Discreet, prepared, and uncompromising representation from arraignment through resolution.',
  'Estate Planning': 'The decisions you make today protect the people you love tomorrow. Wills, trusts, and succession structured with care.',
  'Family Law': 'Divorce, custody, and support handled with composure and the long view in mind. Your family\'s next chapter, with counsel.',
  'Business Law': 'Formation through exit. The contracts, governance, and disputes that determine whether a venture endures.',
  'Real Estate': 'Transactions, title disputes, and landlord-tenant matters resolved with the precision the asset deserves.',
  'Immigration': 'Visas, naturalization, and removal defense — patient guidance through a system that doesn\'t reward improvisation.',
  'Employment Law': 'Wrongful termination, discrimination, wage and hour. Your rights at work, asserted with intention.',
  'Bankruptcy': 'Chapter 7 and 13 filings approached as financial reset, not personal failure.',
  'Workers\' Compensation': 'Hurt on the job? The benefits the law promises you, recovered without retaliation.',
}
function copyForArea(area: string): string {
  return AREA_COPY[area]
    || `Experienced counsel in ${area.toLowerCase()}. Strategy tailored to the facts of your situation, not a template.`
}

// Attorney roster — placeholder names paired with their own initial monograms.
// Three is the right number visually for a boutique firm; more starts to feel
// like a directory. Bar admissions add the "this is a real firm" detail that
// generic placeholder pages always miss.
const ATTORNEYS = [
  { name: 'M. Reynolds', role: 'Founding Partner', initials: 'MR', admitted: 'CA · NY · DC' },
  { name: 'J. Donahue',  role: 'Managing Partner', initials: 'JD', admitted: 'CA · 9th Cir.' },
  { name: 'S. Kapoor',   role: 'Senior Counsel',   initials: 'SK', admitted: 'CA · NY' },
]

// Stats with parsed-out numerics so the in-view ticker animates cleanly.
// `prefix` and `suffix` get stitched back around the animated digits; `roman`
// values just appear instantly (Roman numerals don't tick meaningfully).
const STATS: Array<{ value: number; format: 'currency-m' | 'count' | 'rating'; suffix?: string; label: string } | { roman: string; label: string }> = [
  { roman: 'XXVII',                                                        label: 'Years of Practice' },
  { value: 340, format: 'currency-m',                                      label: 'Recovered for Clients' },
  { value: 1200, format: 'count', suffix: '+',                             label: 'Cases Resolved' },
  { value: 4.9, format: 'rating',                                          label: 'Client Rating' },
]

// ── Hooks ────────────────────────────────────────────────────────────────────

// IntersectionObserver-based "has the user seen this yet" hook. Triggers once
// on first entry — animations should not retrigger every time the user
// scrolls past, that's noise. -10% root margin so reveals fire just before
// the section is fully on-screen, which feels more deliberate.
function useInView<T extends HTMLElement>(opts?: { rootMargin?: string }) {
  const ref = useRef<T | null>(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true)
          io.disconnect()
        }
      },
      { rootMargin: opts?.rootMargin ?? '-10% 0px -10% 0px', threshold: 0.05 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [seen, opts?.rootMargin])
  return [ref, seen] as const
}

// Honors prefers-reduced-motion. We don't kill animations entirely (a fade
// still aids comprehension) — we just disable transform-based motion in the
// places it'd be jarring. Returns `true` when motion should be reduced.
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return reduced
}

// Number ticker. requestAnimationFrame-driven count up using an Emil-approved
// strong ease-out curve. Decimal-aware (4.9 → renders as "4.9"). Skips the
// animation entirely when the user prefers reduced motion — the final value
// just appears.
function StatTicker({
  target,
  format,
  active,
  duration = 1400,
}: {
  target: number
  format: 'currency-m' | 'count' | 'rating'
  active: boolean
  duration?: number
}) {
  const reduced = useReducedMotion()
  const [v, setV] = useState(reduced || !active ? (active ? target : 0) : 0)
  const startRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!active) return
    if (reduced) { setV(target); return }
    const tick = (ts: number) => {
      if (startRef.current == null) startRef.current = ts
      const t = Math.min(1, (ts - startRef.current) / duration)
      // cubic-bezier(0.23, 1, 0.32, 1) approximation: strong ease-out
      const eased = 1 - Math.pow(1 - t, 4)
      setV(target * eased)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [active, target, duration, reduced])

  if (format === 'currency-m') return <>${Math.round(v)}M</>
  if (format === 'rating')     return <>{v.toFixed(1)}</>
  // count
  return <>{Math.round(v).toLocaleString()}</>
}

// Stats band with intersection-triggered tickers. Pulled out as its own
// component so the IntersectionObserver only watches one element instead of
// each StatTicker setting up its own observer.
function StatsBand() {
  const [ref, seen] = useInView<HTMLDivElement>()
  return (
    <section
      ref={ref}
      className={`ll-reveal ${seen ? 'is-seen' : ''}`}
      style={{
        borderTop: '1px solid rgba(246,241,232,0.10)',
        borderBottom: '1px solid rgba(246,241,232,0.10)',
        background: 'rgba(255,255,255,0.025)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-y-10">
        {STATS.map((s, i) => (
          <div key={i} className="px-6" style={{ borderLeft: '1px solid rgba(201,169,106,0.55)' }}>
            <div
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontSize: '2.3rem',
                fontWeight: 600,
                color: '#f6f1e8',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {'roman' in s
                ? s.roman
                : <><StatTicker target={s.value} format={s.format} active={seen} />{s.suffix || ''}</>}
              {'format' in s && s.format === 'rating' && <span style={{ color: '#c9a96a', marginLeft: '0.35rem' }}>★</span>}
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] mt-3" style={{ color: 'rgba(246,241,232,0.55)' }}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// Wraps a section's contents so they fade-rise into place once the user
// actually scrolls them into view. `stagger` gates the children with a
// cascading delay (60ms apart). Both modes ride the same custom ease curve.
function RevealSection({
  children,
  stagger = false,
  className = '',
  ...rest
}: { children: ReactNode; stagger?: boolean; className?: string } & HTMLAttributes<HTMLDivElement>) {
  const [ref, seen] = useInView<HTMLDivElement>()
  const cls = `${stagger ? 'll-reveal-stagger' : 'll-reveal'} ${seen ? 'is-seen' : ''} ${className}`
  return (
    <div ref={ref} className={cls} {...rest}>
      {children}
    </div>
  )
}

export default function SandboxPreviewPage() {
  const { slug } = useParams<{ slug: string }>()
  const brand = useBrand()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const widgetMounted = useRef(false)

  // Load Google Fonts once. Survives StrictMode double-effect via the id check.
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return
    const link = document.createElement('link')
    link.id = FONT_LINK_ID
    link.rel = 'stylesheet'
    link.href = FONT_HREF
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    if (!slug) {
      setError('Missing preview slug')
      return
    }
    fetch(`${API_BASE_URL}/api/affiliates/public/sandbox-preview/${encodeURIComponent(slug)}`)
      .then(async r => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}))
          throw new Error(data?.message || `Preview not available (${r.status})`)
        }
        return r.json()
      })
      .then(data => setPreview({
        widgetKey: data.widgetKey,
        firmName: data.firmName,
        primaryColor: data.primaryColor || '#0e1a2b',
        welcomeMessage: data.welcomeMessage,
        assistantName: data.assistantName,
        practiceAreas: Array.isArray(data.practiceAreas) ? data.practiceAreas : [],
        tagline: data.tagline || 'Trusted Counsel. Real Results.',
        wizardConfigured: !!data.wizardConfigured,
      }))
      .catch(err => setError(err.message || 'Preview unavailable'))
  }, [slug])

  // Inject the LegalLady chat widget exactly once.
  //
  // We deliberately DON'T use the script-tag autoInit path (which reads
  // data-client-id off the <script>) because autoInit doesn't honor
  // data-auto-open — autoOpen has to come through the constructor config.
  // Instead we follow the production-embed pattern from WidgetPage.tsx:
  // load the IIFE, then on `onload` manually instantiate `new Chat({...})`
  // with autoOpen:true so the chat panel pops open as soon as a prospect
  // lands. Without `data-client-id` on the tag, autoInit's "any clientId?"
  // check fails and it skips mounting, so there's no double-init.
  useEffect(() => {
    if (!preview || widgetMounted.current) return
    widgetMounted.current = true

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `${API_BASE_URL}/widget/style.css`
    document.head.appendChild(link)

    const script = document.createElement('script')
    script.src = `${API_BASE_URL}/widget/intake-chat.iife.js`
    script.async = true
    script.onload = () => {
      try {
        const w = window as any
        // The IIFE exposes IntakeChat both as a constructor and as a module
        // namespace ({ IntakeChat: ... }). Handle both shapes the same way
        // WidgetPreview / WidgetPage do.
        const Chat = w.IntakeChat?.IntakeChat || w.IntakeChat
        if (typeof Chat !== 'function') {
          console.warn('[SandboxPreview] IntakeChat constructor not found on window')
          return
        }
        // eslint-disable-next-line no-new
        new Chat({
          clientId: preview.widgetKey,
          apiUrl: API_BASE_URL,
          websocketUrl: API_BASE_URL,
          position: 'bottom-right',
          theme: 'light',
          autoOpen: true,            // pop the panel automatically — this is a demo
          attentionGrabber: true,    // bouncy entrance for the bubble
          // Action bar disabled for the demo. The default actions ('book',
          // 'call') float as small icons above the chat bubble and the user
          // sees them as "ghost buttons that don't do much" — call copies a
          // phone number, book opens a calendar that isn't connected to the
          // sandbox. Browser voice is reachable via the dedicated mic icon
          // in the chat header (only renders when webVoiceEnabled is true).
          showActionBar: false,
          usePageContext: true,      // include page title/url in widget context
          branding: {
            companyName: preview.firmName,
            primaryColor: preview.primaryColor,
          },
        })
      } catch (err) {
        console.error('[SandboxPreview] Widget init failed:', err)
      }
    }
    script.onerror = () => {
      console.error(`[SandboxPreview] Widget script failed to load from ${API_BASE_URL}/widget/intake-chat.iife.js`)
    }

    document.body.appendChild(script)

    return () => {
      script.remove()
      link.remove()
    }
  }, [preview])

  const initials = useMemo(() => deriveInitials(preview?.firmName || ''), [preview?.firmName])
  const taglineLines = useMemo(() => {
    if (!preview) return []
    return preview.tagline.split('.').filter(s => s.trim()).map(s => s.trim())
  }, [preview])

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: '#f6f1e8', fontFamily: '"Manrope", system-ui, sans-serif' }}
      >
        <div className="max-w-md text-center">
          <h1 className="text-3xl mb-3" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#0e1a2b' }}>
            Demo unavailable
          </h1>
          <p className="text-stone-500 text-sm">{error}</p>
        </div>
      </div>
    )
  }

  if (!preview) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: '#f6f1e8', fontFamily: '"Manrope", system-ui, sans-serif' }}
      >
        <div className="text-stone-400 text-xs uppercase tracking-[0.3em]">Loading</div>
      </div>
    )
  }

  const { firmName, practiceAreas } = preview
  const year = new Date().getFullYear()

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: '#0a0e1f',
        color: '#f6f1e8',
        fontFamily: '"Manrope", system-ui, sans-serif',
        fontWeight: 400,
      }}
    >
      {/* Faded legal-architecture image set behind the page. If the Unsplash
          URL fails to load, the dark gradient overlay still produces an
          intentional-looking atmosphere — the image is decorative depth,
          never load-bearing. position:fixed (no `background-attachment:fixed`,
          which janks on iOS) so the image stays put while content scrolls. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          backgroundImage:
            "linear-gradient(180deg, rgba(10,14,31,0.78) 0%, rgba(10,14,31,0.92) 55%, rgba(10,14,31,1) 100%), " +
            "url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&q=70&auto=format&fit=crop')",
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          filter: 'saturate(0.55) brightness(0.55)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      {/* Royal-blue radial accent — pulls brand warmth into the dark */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '18%',
          width: 'min(1100px, 95vw)',
          height: '720px',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(ellipse at center, rgba(77,113,253,0.18), rgba(77,113,253,0) 60%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      {/* Wrapper to lift content above the absolute decoration */}
      <div className="relative" style={{ zIndex: 2 }}>

      {/* ── Motion + atmosphere ─────────────────────────────────────────
        Easing curves match Emil's recommendations (strong ease-out for UI).
        Reveal animations gated to first viewport entry via .is-seen — never
        re-trigger. Hover effects gated under (hover: hover) so touch devices
        don't get false-positive hovers on tap. All durations ≤ 600ms (page
        load) or ≤ 300ms (UI). prefers-reduced-motion strips transform-based
        motion; opacity/color stays so reveals still aid comprehension. */}
      <style>{`
        :root {
          --ll-ease-out:   cubic-bezier(0.23, 1, 0.32, 1);
          --ll-ease-in-out: cubic-bezier(0.77, 0, 0.175, 1);
          --ll-ink:    #0e1a2b;
          --ll-paper:  #f6f1e8;
          --ll-gold:   #c9a96a;
          --ll-cream:  #e8dcc4;
        }

        /* Initial-page-load staggered reveals (only fires once on mount) */
        @keyframes ll-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ll-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .ll-rise { animation: ll-rise 700ms var(--ll-ease-out) both; }
        .ll-fade { animation: ll-fade 900ms ease-out both; }
        .ll-d-1  { animation-delay: 80ms; }
        .ll-d-2  { animation-delay: 160ms; }
        .ll-d-3  { animation-delay: 240ms; }
        .ll-d-4  { animation-delay: 340ms; }
        .ll-d-5  { animation-delay: 440ms; }

        /* IntersectionObserver-driven scroll reveals.
           Default state: invisible, slight Y offset. .is-seen: settled. */
        .ll-reveal {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 600ms var(--ll-ease-out), transform 600ms var(--ll-ease-out);
          will-change: opacity, transform;
        }
        .ll-reveal.is-seen { opacity: 1; transform: translateY(0); }
        .ll-reveal-stagger > * {
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 500ms var(--ll-ease-out), transform 500ms var(--ll-ease-out);
        }
        .ll-reveal-stagger.is-seen > * { opacity: 1; transform: translateY(0); }
        .ll-reveal-stagger.is-seen > *:nth-child(1) { transition-delay: 60ms; }
        .ll-reveal-stagger.is-seen > *:nth-child(2) { transition-delay: 120ms; }
        .ll-reveal-stagger.is-seen > *:nth-child(3) { transition-delay: 180ms; }
        .ll-reveal-stagger.is-seen > *:nth-child(4) { transition-delay: 240ms; }
        .ll-reveal-stagger.is-seen > *:nth-child(5) { transition-delay: 300ms; }
        .ll-reveal-stagger.is-seen > *:nth-child(6) { transition-delay: 360ms; }

        /* Hairline rule, used in eyebrows, dividers, and hero accents */
        .ll-hairline {
          background-image: linear-gradient(to right, rgba(14,26,43,0.20), rgba(14,26,43,0.04));
          height: 1px;
        }
        /* Dark-mode variant — used wherever the section sits on the navy bg */
        .ll-hairline-on-dark {
          background-image: linear-gradient(to right, rgba(246,241,232,0.40), rgba(246,241,232,0.04));
          height: 1px;
        }

        /* Eyebrow letter-spacing — separates from labels using regular tracking */
        .ll-num-label { letter-spacing: 0.32em; }

        /* Animated underline reveal on links (text-decoration is too rigid) */
        .ll-link-underline {
          background-image: linear-gradient(currentColor, currentColor);
          background-size: 0% 1px;
          background-repeat: no-repeat;
          background-position: 0 100%;
          transition: background-size 320ms var(--ll-ease-out);
        }
        @media (hover: hover) and (pointer: fine) {
          .ll-link-underline:hover { background-size: 100% 1px; }
        }

        /* Primary CTA — outline, gap-grow on hover, scale-press on active */
        .ll-cta {
          transition: gap 280ms var(--ll-ease-out),
                      background-color 200ms ease,
                      color 200ms ease,
                      border-color 200ms ease,
                      transform 160ms var(--ll-ease-out);
        }
        @media (hover: hover) and (pointer: fine) {
          .ll-cta:hover { gap: 1.4rem; }
        }
        .ll-cta:active { transform: scale(0.97); }

        /* Practice-area card hover: hairline sweep across the bottom edge.
           clip-path lets us animate a "fill" that doesn't trigger layout. */
        .ll-area-rule {
          position: absolute;
          left: 0; right: 0; bottom: -1px;
          height: 1px;
          background: var(--ll-gold);
          clip-path: inset(0 100% 0 0);
          transition: clip-path 380ms var(--ll-ease-out);
        }
        @media (hover: hover) and (pointer: fine) {
          .ll-area:hover .ll-area-rule { clip-path: inset(0 0 0 0); }
          .ll-area:hover .ll-area-title { color: var(--ll-gold); }
        }
        .ll-area-title { transition: color 240ms var(--ll-ease-out); }

        /* Attorney monogram card */
        .ll-counsel-card { transition: transform 320ms var(--ll-ease-out); }
        @media (hover: hover) and (pointer: fine) {
          .ll-counsel-card:hover { transform: translateY(-3px); }
          .ll-counsel-card:hover .ll-counsel-frame {
            border-color: rgba(201,169,106,0.55);
          }
          .ll-counsel-card:hover .ll-counsel-mono {
            color: rgba(201,169,106,0.55);
          }
        }
        .ll-counsel-frame { transition: border-color 320ms var(--ll-ease-out); }
        .ll-counsel-mono  { transition: color 320ms var(--ll-ease-out); }

        /* Vertical decorative rail in the hero */
        .ll-rail {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          letter-spacing: 0.32em;
          font-size: 10px;
          text-transform: uppercase;
          color: rgba(14, 26, 43, 0.45);
        }

        /* Film-grain noise overlay for dark theme. screen blend lifts subtle
           light specks on the navy bg so the page reads as photographic
           atmosphere instead of flat digital color. */
        .ll-grain {
          position: fixed;
          inset: 0;
          pointer-events: none;
          opacity: 0.10;
          mix-blend-mode: screen;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
          z-index: 3;
        }

        /* Soft gold radial glow behind the hero headline to add depth */
        .ll-hero-aura {
          position: absolute;
          left: 50%;
          top: 30%;
          width: min(900px, 90vw);
          height: 600px;
          transform: translate(-50%, -50%);
          background: radial-gradient(ellipse at center, rgba(201,169,106,0.18), rgba(201,169,106,0) 60%);
          pointer-events: none;
          z-index: 0;
        }

        /* Reduced-motion: keep fades, drop transforms */
        @media (prefers-reduced-motion: reduce) {
          .ll-rise, .ll-fade,
          .ll-reveal, .ll-reveal-stagger > * { transform: none !important; }
          .ll-counsel-card:hover { transform: none !important; }
          .ll-cta:active { transform: none !important; }
        }
      `}</style>

      <div className="ll-grain" aria-hidden />

      {/* ── Utility bar ───────────────────────────────────────────────── */}
      <div
        className="ll-fade text-[11px] tracking-[0.18em] uppercase"
        style={{ background: 'rgba(0,0,0,0.4)', color: 'rgba(246,241,232,0.85)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-2.5 flex items-center justify-between">
          <span>Available · 24 / 7 · Free Consultation</span>
          <span className="hidden sm:inline" style={{ color: '#c9a96a' }}>(310) 555 · 0144</span>
        </div>
      </div>

      {/* ── Main nav ──────────────────────────────────────────────────── */}
      <header
        className="ll-fade ll-d-1 sticky top-0 z-30 backdrop-blur-md"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(10,14,31,0.78)' }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Monogram logo — the affiliate's initials in a hairline square */}
            <div
              className="w-12 h-12 flex items-center justify-center"
              style={{ border: '1px solid rgba(246,241,232,0.6)' }}
              aria-hidden
            >
              <span
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '17px',
                  fontWeight: 500,
                  letterSpacing: '0.18em',
                  color: '#f6f1e8',
                  paddingLeft: '0.18em',
                }}
              >
                {initials}
              </span>
            </div>
            <div className="leading-tight">
              <div
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '22px',
                  fontWeight: 600,
                  color: '#f6f1e8',
                  letterSpacing: '0.01em',
                }}
              >
                {firmName}
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] mt-0.5" style={{ color: 'rgba(246,241,232,0.55)' }}>
                Attorneys at Law · Est. 1992
              </div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-[11px] uppercase tracking-[0.22em]" style={{ color: 'rgba(246,241,232,0.78)' }}>
            <a className="ll-link-underline cursor-pointer">Practice</a>
            <a className="ll-link-underline cursor-pointer">People</a>
            <a className="ll-link-underline cursor-pointer">Outcomes</a>
            <a className="ll-link-underline cursor-pointer">Insights</a>
            <a className="ll-link-underline cursor-pointer">Contact</a>
          </nav>

          <button
            className="hidden md:inline-flex items-center px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] ll-cta font-semibold"
            style={{ border: '1px solid rgba(246,241,232,0.6)', color: '#f6f1e8', background: 'transparent' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#c9a96a'
              e.currentTarget.style.borderColor = '#c9a96a'
              e.currentTarget.style.color = '#0a0e1f'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.borderColor = 'rgba(246,241,232,0.6)'
              e.currentTarget.style.color = '#f6f1e8'
            }}
          >
            Schedule Consultation
          </button>
        </div>
      </header>

      {/* ── Hero · asymmetric editorial ───────────────────────────────── */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-24 sm:pt-32 pb-20 sm:pb-28">
        <div className="ll-hero-aura" aria-hidden />

        {/* Vertical decorative rail — "Chambers · Established 1992 · Los
            Angeles" running up the left edge. The kind of detail real boutique
            firms put on their About page; here it elevates the hero from
            generic SaaS template to "this is a serious firm" without saying so. */}
        <div
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 pl-2 ll-fade ll-d-2"
          style={{ height: '200px', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
          aria-hidden
        >
          <span className="ll-rail">Chambers · Est. 1992 · Los Angeles</span>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10 items-end" style={{ zIndex: 2 }}>
          <div className="md:col-span-8">
            <div className="ll-rise ll-d-2 text-[10px] uppercase ll-num-label mb-10 flex items-center gap-3" style={{ color: 'rgba(246,241,232,0.55)' }}>
              <span style={{ color: '#c9a96a' }}>No. 01</span>
              <span className="ll-hairline-on-dark w-12 inline-block" />
              <span>Counsel</span>
            </div>
            <h1
              className="ll-rise ll-d-3"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 600,
                fontSize: 'clamp(3rem, 8vw, 7.5rem)',
                lineHeight: 0.96,
                letterSpacing: '-0.02em',
                color: '#f6f1e8',
                textShadow: '0 2px 40px rgba(0,0,0,0.4)',
              }}
            >
              {(taglineLines.length > 0 ? taglineLines : ['Trusted counsel', 'Real results']).map((line, i, arr) => (
                <span key={i} className="block">
                  {line}
                  {i === arr.length - 1
                    ? <span style={{ color: '#c9a96a' }}>.</span>
                    : <span style={{ color: '#f6f1e8' }}>.</span>}
                </span>
              ))}
            </h1>
            <div className="ll-rise ll-d-4 mt-10 flex items-center gap-4 text-[10px] uppercase ll-num-label" style={{ color: 'rgba(246,241,232,0.55)' }}>
              <span className="ll-hairline-on-dark w-12 inline-block" />
              <span>AV Preeminent · Martindale-Hubbell</span>
            </div>
          </div>

          <div className="md:col-span-4 md:pl-8 ll-rise ll-d-4">
            <div className="ll-hairline-on-dark mb-8" />
            <p className="leading-[1.7] text-[15px] mb-10" style={{ fontWeight: 400, color: 'rgba(246,241,232,0.78)' }}>
              <span style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: '1.15em', color: '#f6f1e8' }}>
                {firmName}
              </span>{' '}
              represents clients with the dedication and discretion they deserve. Free consultations, available twenty-four hours a day.
            </p>
            <button
              className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] pb-2 ll-cta font-semibold"
              style={{ borderBottom: '1px solid #c9a96a', color: '#c9a96a' }}
            >
              Schedule a Consultation
              <span style={{ color: '#c9a96a', fontSize: '14px' }}>→</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Stats · count up on viewport entry ────────────────────────── */}
      <StatsBand />

      {/* ── Practice areas ────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
        <div className="flex items-end justify-between mb-16 pb-6" style={{ borderBottom: '1px solid rgba(246,241,232,0.10)' }}>
          <div>
            <div className="text-[10px] uppercase ll-num-label mb-3 flex items-center gap-3" style={{ color: 'rgba(246,241,232,0.55)' }}>
              <span style={{ color: '#c9a96a' }}>No. 02</span>
              <span className="ll-hairline-on-dark w-12 inline-block" />
              <span>Practice</span>
            </div>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 600,
                fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
                color: '#f6f1e8',
                letterSpacing: '-0.01em',
              }}
            >
              Areas of focus<span style={{ color: '#c9a96a' }}>.</span>
            </h2>
          </div>
          <div className="hidden md:block text-[11px] uppercase tracking-[0.22em] max-w-[14rem] text-right leading-relaxed" style={{ color: 'rgba(246,241,232,0.55)' }}>
            Selective by design. Each matter receives a partner's attention.
          </div>
        </div>

        <RevealSection stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
          {practiceAreas.map((area, idx) => (
            <article key={area} className="ll-area group relative pb-8">
              <div
                className="text-[11px] tracking-[0.32em] mb-5"
                style={{ color: '#c9a96a', fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontWeight: 400 }}
              >
                {toRoman(idx + 1)}
              </div>
              <h3
                className="ll-area-title mb-4"
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.65rem',
                  color: '#f6f1e8',
                  letterSpacing: '-0.005em',
                }}
              >
                {area}
              </h3>
              <p className="text-[14px] leading-[1.75]" style={{ fontWeight: 400, color: 'rgba(246,241,232,0.72)' }}>
                {copyForArea(area)}
              </p>
              <a className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] ll-link-underline cursor-pointer" style={{ color: 'rgba(246,241,232,0.85)' }}>
                Read more <span style={{ color: '#c9a96a' }}>→</span>
              </a>
              <span className="ll-area-rule" aria-hidden />
            </article>
          ))}
        </RevealSection>
      </section>

      {/* ── Counsel · attorney roster · slight contrast band ──────────── */}
      <section style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.45))', borderTop: '1px solid rgba(246,241,232,0.08)', borderBottom: '1px solid rgba(246,241,232,0.08)', color: 'rgba(246,241,232,0.92)' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 sm:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="text-[10px] uppercase ll-num-label mb-3 flex items-center gap-3" style={{ color: 'rgba(201,169,106,0.85)' }}>
              <span>No. 03</span>
              <span className="w-12 inline-block" style={{ background: 'linear-gradient(to right, rgba(201,169,106,0.55), rgba(201,169,106,0.05))', height: '1px' }} />
              <span style={{ color: 'rgba(232,220,196,0.65)' }}>Counsel</span>
            </div>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 300,
                fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
                color: '#f6f1e8',
                letterSpacing: '-0.01em',
                lineHeight: 1.05,
              }}
            >
              The team behind your case<span style={{ color: '#c9a96a' }}>.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-[1.75] max-w-lg" style={{ color: 'rgba(232,220,196,0.75)', fontWeight: 300 }}>
              A small bench of senior attorneys, supported by a research team trained for the matters that don't fit a template.
            </p>
          </div>

          <RevealSection stagger className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {ATTORNEYS.map((att) => (
              <div key={att.name} className="ll-counsel-card">
                <div
                  className="ll-counsel-frame aspect-[4/5] mb-6 flex items-center justify-center"
                  style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(232,220,196,0.12)',
                  }}
                >
                  <span
                    className="ll-counsel-mono"
                    style={{
                      fontFamily: '"Cormorant Garamond", Georgia, serif',
                      fontSize: 'clamp(4rem, 9vw, 7rem)',
                      fontWeight: 300,
                      color: 'rgba(201,169,106,0.32)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {att.initials}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: '1.45rem',
                    fontWeight: 500,
                    color: '#f6f1e8',
                  }}
                >
                  {att.name}
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] mt-2" style={{ color: 'rgba(232,220,196,0.55)' }}>
                  {att.role}
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] mt-3" style={{ color: 'rgba(201,169,106,0.65)' }}>
                  Admitted · {att.admitted}
                </div>
              </div>
            ))}
          </RevealSection>
        </div>
      </section>

      {/* ── Testimonial ───────────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 sm:px-10 py-24 sm:py-32 text-center">
        <RevealSection>
          <div
            aria-hidden
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: '5rem',
              color: '#c9a96a',
              lineHeight: 0.6,
              fontStyle: 'italic',
            }}
            className="mb-6"
          >
            &ldquo;
          </div>
          <blockquote
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(1.4rem, 2.6vw, 2rem)',
              lineHeight: 1.45,
              fontWeight: 500,
              color: '#f6f1e8',
              fontStyle: 'italic',
            }}
          >
            They handled what felt like the hardest moment of our lives with grace, urgency, and an absolute command of the law. We came out the other side whole.
          </blockquote>
          <div className="mt-10 text-[10px] uppercase ll-num-label flex items-center justify-center gap-3" style={{ color: 'rgba(246,241,232,0.55)' }}>
            <span className="ll-hairline-on-dark w-12" />
            <span>Verified Client · Personal Injury · {year - 1}</span>
            <span className="ll-hairline-on-dark w-12" />
          </div>
        </RevealSection>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer style={{ background: 'rgba(0,0,0,0.55)', borderTop: '1px solid rgba(246,241,232,0.08)', color: 'rgba(246,241,232,0.78)' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-7">
              <div
                className="w-11 h-11 flex items-center justify-center"
                style={{ border: '1px solid rgba(232,220,196,0.45)' }}
              >
                <span
                  style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    letterSpacing: '0.18em',
                    color: '#f6f1e8',
                    paddingLeft: '0.18em',
                  }}
                >
                  {initials}
                </span>
              </div>
              <div
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '20px',
                  fontWeight: 500,
                  color: '#f6f1e8',
                }}
              >
                {firmName}
              </div>
            </div>
            <p className="text-[14px] leading-[1.75] max-w-md" style={{ fontWeight: 300 }}>
              Boutique counsel. Personal attention. Available around the clock when the matter doesn't wait for business hours.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] uppercase ll-num-label mb-5" style={{ color: 'rgba(201,169,106,0.85)' }}>Visit</div>
            <p className="text-[14px] leading-[1.85]" style={{ fontWeight: 300 }}>
              Suite 1700<br />
              1100 Wilshire Boulevard<br />
              Los Angeles, CA 90017
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] uppercase ll-num-label mb-5" style={{ color: 'rgba(201,169,106,0.85)' }}>Reach</div>
            <p className="text-[14px] leading-[1.85]" style={{ fontWeight: 300 }}>
              <span style={{ color: '#f6f1e8' }}>(310) 555 · 0144</span><br />
              contact@{firmName.toLowerCase().replace(/[^a-z]/g, '').slice(0, 14) || 'firm'}.law<br />
              24 Hours · 7 Days
            </p>
          </div>
        </div>

        <div
          className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[10.5px] uppercase tracking-[0.22em]"
          style={{ borderTop: '1px solid rgba(232,220,196,0.12)', color: 'rgba(232,220,196,0.45)' }}
        >
          <span>© {year} {firmName} · Attorney Advertising</span>
          <span>Demo · Powered by {brand.appName} · Try the chat widget ↘</span>
        </div>
      </footer>

      <div id="intake-chat-widget" />
      </div>
    </div>
  )
}
